import React, { useState } from 'react';
import { reauthenticateWithCredential, updatePassword, EmailAuthProvider } from 'firebase/auth';
import { auth } from '@discovr/common';
import { toast } from 'sonner';
import { Eye, EyeSlash } from '../../design-system/icons';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { usePasswordPolicy } from './usePasswordPolicy';
import { PasswordRequirements } from './PasswordRequirements';

export interface ChangePasswordDialogProps {
  userEmail: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface FieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  show: boolean;
  onToggleShow: () => void;
  disabled: boolean;
  error?: string;
}

function PasswordField({ id, label, value, onChange, show, onToggleShow, disabled, error }: FieldProps) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <div className="relative">
        <Input
          id={id}
          placeholder="Enter your password"
          type={show ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className="pr-9"
          required
        />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          tabIndex={-1}
          onClick={onToggleShow}
          className="absolute top-1/2 right-1 -translate-y-1/2 size-7 text-muted-foreground hover:text-foreground"
          aria-label={show ? 'Hide' : 'Show'}
        >
          {show ? <EyeSlash size={14} /> : <Eye size={14} />}
        </Button>
      </div>
      {error && <p role="alert" className="text-caption-regular text-rose-600">{error}</p>}
    </div>
  );
}

export function ChangePasswordDialog({ userEmail, open, onOpenChange }: ChangePasswordDialogProps) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [currentError, setCurrentError] = useState('');
  const [lengthError, setLengthError] = useState('');
  const [confirmError, setConfirmError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    requirements,
    isValid: isNextValid,
    errorMessage: nextError,
  } = usePasswordPolicy(next);

  const reset = () => {
    setCurrent(''); setNext(''); setConfirm('');
    setShowCurrent(false); setShowNext(false); setShowConfirm(false);
    setCurrentError(''); setLengthError(''); setConfirmError('');
  };

  const handleOpenChange = (v: boolean) => {
    if (!v) reset();
    onOpenChange(v);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentError(''); setLengthError(''); setConfirmError('');

    if (!isNextValid) { setLengthError(nextError ?? 'Please meet all password requirements.'); return; }
    if (next !== confirm) { setConfirmError("Passwords don't match"); return; }

    setIsSubmitting(true);
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('Not signed in');
      await reauthenticateWithCredential(user, EmailAuthProvider.credential(userEmail, current));
      await updatePassword(user, next);
      toast.success('Password updated');
      handleOpenChange(false);
    } catch (err: unknown) {
      const code = (err as { code?: string }).code ?? '';
      if (code === 'auth/wrong-password' || code === 'auth/invalid-credential') {
        setCurrentError('Current password is incorrect');
      } else {
        toast.error(err instanceof Error ? err.message : 'Failed to update password');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Change password</DialogTitle>
          <DialogDescription>Choose a strong password you don&apos;t use elsewhere.</DialogDescription>
        </DialogHeader>
        <form onSubmit={(e) => { void handleSubmit(e); }} className="flex flex-col gap-4 pt-2">
          <PasswordField
            id="cp-current"
            label="Current password"
            value={current}
            onChange={setCurrent}
            show={showCurrent}
            onToggleShow={() => setShowCurrent((v) => !v)}
            disabled={isSubmitting}
            error={currentError}
          />
          <div className="flex flex-col gap-1.5">
            <PasswordField
              id="cp-new"
              label="New password"
              value={next}
              onChange={setNext}
              show={showNext}
              onToggleShow={() => setShowNext((v) => !v)}
              disabled={isSubmitting}
              error={lengthError}
            />
            {next.length > 0 && <PasswordRequirements requirements={requirements} />}
          </div>
          <PasswordField
            id="cp-confirm"
            label="Confirm password"
            value={confirm}
            onChange={setConfirm}
            show={showConfirm}
            onToggleShow={() => setShowConfirm((v) => !v)}
            disabled={isSubmitting}
            error={confirmError}
          />
          <div className="flex justify-end pt-2">
            <Button type="submit" disabled={isSubmitting || !isNextValid}>
              {isSubmitting ? 'Updating…' : 'Update password'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
